import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";

export interface MyHardwareForm {
  id?: string;
  typeHardwareId: number | string;
  subtypeHardware: string | { name: string } | null;
  subtypeHardwareId: number | string;
  datacenterId: string;
  typeHardware: string | { name: string } | null;
  serialNumber: string;
  model: string;
  vendor: string;
  hostname: string;
  installedDate: string;
  endOfLifeDate: string;
  rackId: string;
  rackUnit: string;
  status: string;
  phase: string;
  template: string;
  targetDeviceId?: number | null;
  slotAssignmentId?: number | null;
}

export interface HardwareCreateModalProps {
  isOpen: boolean;
  isLoading: boolean;
  editMode: boolean;
  hwForm: MyHardwareForm;
  currentDcName: string;
  dataCenterOptions: string[]; // Datacenter options without "All"
  typeHardwareOptions: string[];
  subTypeOptions: string[];
  hostnameOptions: string[];
  slotOptions: string[];
  hostnameId: number | null;
  slotId: number | null;
  isLoadingSubtypes?: boolean;
  hardwareFormFields: readonly {
    key: string;
    label: string;
    placeholder: string;
  }[];
  getTypeHardwareString: (value: string | { name: string } | null) => string;
  isSaving?: boolean;
  isCreating?: boolean;
  onClose: () => void;
  onSave: () => void;
  onFieldChange: (fieldKey: keyof MyHardwareForm, newValue: string) => void;
  onDataCenterSelect?: (dcName: string) => void;
  onSelectType: (selectedTypeName: string | { name: string }) => void;
  onSelectSubType: (selectedSubType: string | { name: string }) => void;
  onHostnameSelect: (chosenName: string) => void;
  onSlotSelect: (label: string) => void;
  getHostnameLabelById: (hwId: number) => string;
  getSlotLabelById: (id: number) => string;
}

export const HardwareCreateModal: React.FC<HardwareCreateModalProps> = ({
  isOpen,
  isLoading,
  editMode,
  hwForm,
  currentDcName,
  dataCenterOptions,
  typeHardwareOptions,
  subTypeOptions,
  hostnameOptions,
  slotOptions,
  hostnameId,
  slotId,
  isLoadingSubtypes = false,
  hardwareFormFields,
  getTypeHardwareString,
  isSaving = false,
  isCreating = false,
  onClose,
  onSave,
  onFieldChange,
  onDataCenterSelect,
  onSelectType,
  onSelectSubType,
  onHostnameSelect,
  onSlotSelect,
  getHostnameLabelById,
  getSlotLabelById,
}) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={editMode ? "Edit Hardware" : "Add Hardware"}
      customWidth="max-w-6xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 10 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
          <div>
            <SkeletonBlock h={20} w="40%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6 min-h-[600px] p-5">
          {/* Basic Information */}
          <GenericCard isCollapsible={false} className="p-5">
            <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {/* DataCenter dropdown */}
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium">Data Center</label>
                <GenericDropdown
                  items={dataCenterOptions}
                  defaultLabel="Select Data Center"
                  value={currentDcName}
                  onSelect={(dcName) => {
                    if (onDataCenterSelect) {
                      onDataCenterSelect(dcName);
                    }
                  }}
                  maxWidthClass="w-full"
                />
              </div>

              {/* TYPE dropdown */}
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium">Type</label>
                <GenericDropdown
                  items={typeHardwareOptions}
                  defaultLabel="Select Type"
                  value={getTypeHardwareString(hwForm.typeHardware)}
                  onSelect={onSelectType}
                  maxWidthClass="w-full"
                />
              </div>

              {/* SUBTYPE dropdown */}
              <div className="flex flex-col space-y-2">
                <label className="text-sm font-medium">SubType</label>
                <GenericDropdown
                  items={isLoadingSubtypes ? [] : subTypeOptions}
                  defaultLabel={isLoadingSubtypes ? "Loading..." : "Select SubType"}
                  value={getTypeHardwareString(hwForm.subtypeHardware) || ""}
                  onSelect={onSelectSubType}
                  disabled={!hwForm.typeHardware || isLoadingSubtypes}
                  maxWidthClass="w-full"
                />
              </div>

              {/* Form fields - Exclude rackId, rackUnit, phase, template from Basic Information */}
              {hardwareFormFields
                .filter((field) => 
                  field.key !== "rackId" && 
                  field.key !== "rackUnit" && 
                  field.key !== "phase" && 
                  field.key !== "template"
                )
                .map((field) => {
                // 1) Hide hostname field if it's a "module" (it will be in Module Configuration)
                if (hwForm.typeHardware === "Module" && field.key === "hostname") {
                  return null; // Skip rendering
                }

                // 2) If "status", render the status dropdown
                if (field.key === "status") {
                  return (
                    <div className="flex flex-col space-y-2" key={field.key}>
                      <label className="text-sm font-medium">{field.label}</label>
                      <GenericDropdown
                        items={["Installed", "Assigned", "Provisioned", "Active"]}
                        value={hwForm.status ?? ""}
                        defaultLabel="Select Status"
                        onSelect={(val) => onFieldChange("status", val)}
                        maxWidthClass="w-full"
                      />
                    </div>
                  );
                }

                // 3) If "hostname", render the hostname dropdown (only for non-Module types)
                if (field.key === "hostname") {
                  return (
                    <div className="flex flex-col space-y-2" key={field.key}>
                      <label className="text-sm font-medium">{field.label}</label>
                      <GenericInput
                        label=""
                        placeholder={field.placeholder}
                        value={hwForm.hostname ?? ""}
                        onChange={(val: string) => onFieldChange("hostname", val)}
                      />
                    </div>
                  );
                }

                // 4) Otherwise, render a normal GenericInput (always editable)
                const fieldValue = hwForm[field.key as keyof MyHardwareForm];
                const stringValue = typeof fieldValue === "object" && fieldValue !== null && "name" in fieldValue
                  ? fieldValue.name
                  : fieldValue ?? "";
                
                return (
                  <div className="flex flex-col space-y-2" key={field.key}>
                    <label className="text-sm font-medium">{field.label}</label>
                    <GenericInput
                      label=""
                      placeholder={field.placeholder}
                      value={String(stringValue)}
                      onChange={(val: string) => onFieldChange(field.key as keyof MyHardwareForm, val)}
                    />
                  </div>
                );
              })}
            </div>
          </GenericCard>

          {/* Module-specific fields */}
          {hwForm.typeHardware === "Module" && (
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Module Configuration
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Hostname</label>
                  <GenericDropdown
                    items={hostnameOptions}
                    value={hostnameId ? getHostnameLabelById(hostnameId) : ""}
                    defaultLabel="Select Hostname..."
                    onSelect={onHostnameSelect}
                    maxWidthClass="w-full"
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Slot Assignment</label>
                  <GenericDropdown
                    items={slotOptions}
                    value={slotId ? getSlotLabelById(slotId) : ""}
                    onSelect={onSlotSelect}
                    disabled={!hostnameId}
                    defaultLabel="Select Slot..."
                    maxWidthClass="w-full"
                  />
                </div>
              </div>
            </GenericCard>
          )}

          {/* Infrastructure Information - Only for non-Module types */}
          {hwForm.typeHardware !== "Module" && (
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Infrastructure Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {hardwareFormFields
                  .filter((field) => 
                    field.key === "rackId" || 
                    field.key === "rackUnit" || 
                    field.key === "phase" || 
                    field.key === "template"
                  )
                  .map((field) => {
                    const fieldValue = hwForm[field.key as keyof MyHardwareForm];
                    const stringValue = typeof fieldValue === "object" && fieldValue !== null && "name" in fieldValue
                      ? fieldValue.name
                      : fieldValue ?? "";
                    
                    return (
                      <div className="flex flex-col space-y-2" key={field.key}>
                        <label className="text-sm font-medium">{field.label}</label>
                        <GenericInput
                          label=""
                          placeholder={field.placeholder}
                          value={String(stringValue)}
                          onChange={(val: string) => onFieldChange(field.key as keyof MyHardwareForm, val)}
                        />
                      </div>
                    );
                  })}
              </div>
            </GenericCard>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <GenericButton
          label="Cancel"
          variant="secondary"
          onClick={onClose}
          disabled={isSaving || isCreating}
        />
        <GenericButton
          label={editMode ? "Save" : "Create"}
          variant="brand"
          onClick={onSave}
          disabled={isSaving || isCreating}
          loading={editMode ? isSaving : isCreating}
        />
      </div>
    </GenericModal>
  );
};
